import { useEffect, useRef, useState } from 'react'
import type { DerivedDC } from '@/api/distributionCenters'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DcPercentInput } from './DcAllocationGrid'
import { dcLabel, equalDcSplit, type DcSplit } from './dc-model'
import { PERCENT_DECIMALS, percentUnits } from './model'

export function DcSplitDialog({
  centers,
  count,
  description,
  initial,
  pending = false,
  onClose,
  onApply,
  onCloseAutoFocus,
}: {
  centers: DerivedDC[]
  count: number
  description: string
  initial: DcSplit
  pending?: boolean
  onClose: () => void
  onApply: (split: DcSplit) => void
  onCloseAutoFocus?: (event: Event) => void
}) {
  const [split, setSplit] = useState(initial)
  // A second click before ``pending`` re-renders must not apply twice.
  const applying = useRef(false)
  useEffect(() => {
    // The apply finished (failed attempts keep the dialog open for a retry).
    if (!pending) applying.current = false
  }, [pending])
  // Fixed point at the stored precision: 33.3334 + 33.3333 + 33.3333 is 100%.
  const units = Object.values(split).reduce(
    (sum, value) => sum + percentUnits(value),
    0
  )
  const total = units / 10 ** PERCENT_DECIMALS
  const balanced = units === percentUnits(100)
  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open && !pending) onClose()
      }}
    >
      <DialogContent
        className="max-w-[min(480px,calc(100vw-32px))] overflow-y-auto bg-card"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>Copy allocation to other waves</DialogTitle>
          <DialogDescription>
            {description} Existing percentages in this scope will be replaced.
          </DialogDescription>
        </DialogHeader>
        <div className="divide-y rounded-md border px-3">
          {centers.map(dc => (
            <div
              key={dc.id}
              className="flex items-center justify-between gap-4 py-2"
            >
              <span className="min-w-0" title={dcLabel(dc)}>
                <span className="block text-sm font-medium">{dc.code}</span>
                {dc.name && dc.name !== dc.code && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {dc.name}
                  </span>
                )}
              </span>
              <div className="w-28 shrink-0">
                <DcPercentInput
                  label={`${dcLabel(dc)} bulk allocation percent`}
                  value={split[dc.id] ?? 0}
                  onChange={value => setSplit({ ...split, [dc.id]: value })}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSplit(equalDcSplit(centers))}
          >
            Split equally
          </Button>
          <span
            role="status"
            className={`text-sm font-semibold ${balanced ? 'text-emerald-700' : 'text-destructive'}`}
          >
            {balanced
              ? '100'
              : total.toLocaleString('en-US', {
                  maximumFractionDigits: PERCENT_DECIMALS,
                })}
            % / 100%
          </span>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!balanced || !count || pending}
            onClick={() => {
              if (applying.current) return
              applying.current = true
              onApply(split)
            }}
          >
            Apply to {count} {count === 1 ? 'wave' : 'waves'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
